import { useCallback, useRef, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent, ScrollView, View } from 'react-native';

import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';

import { useActiveChallengesBadge } from '../../hooks/use-active-challenges-badge';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useChallengesUi, useLoyaltyIncentives } from '../../queries/loyalty.queries';

export type Section = 'challenges' | 'offers' | 'rewards';

export const useScrollManager = () => {
  const [activeSection, setActiveSection] = useState<Section>('challenges');
  const scrollViewRef = useRef<ScrollView>(null);
  const sectionPositions = useRef({
    challenges: 0,
    offers: 0,
    rewards: 0,
  });

  const scrollToSection = useCallback((sectionName: Section) => {
    const position = sectionPositions.current[sectionName];
    scrollViewRef.current?.scrollTo({ y: position, animated: true });
  }, []);

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offset = 40;
    const scrollY = event.nativeEvent.contentOffset.y - offset;

    if (scrollY > sectionPositions.current.offers) {
      setActiveSection('rewards');
    } else if (scrollY > sectionPositions.current.challenges) {
      setActiveSection('offers');
    } else {
      setActiveSection('challenges');
    }
  }, []);

  const measureSectionPosition = useCallback(
    (sectionRef: React.RefObject<View>, sectionName: Section) => {
      if (sectionRef.current && scrollViewRef.current) {
        const scrollViewHandle = scrollViewRef.current.getNativeScrollRef();
        if (scrollViewHandle) {
          sectionRef.current.measureLayout(scrollViewHandle, (_, y) => {
            sectionPositions.current = {
              ...sectionPositions.current,
              [sectionName]: y,
            };
          });
        }
      }
    },
    []
  );

  return {
    scrollViewRef,
    activeSection,
    scrollToSection,
    onScroll,
    measureSectionPosition,
    sectionPositions,
  };
};

export const useIsLoyaltyPageLoading = () => {
  const { loyaltyUserReady } = useLoyaltyLegacyStates();

  const { isLoading: isChallengesLoading } = useChallengesUi();
  const { isLoading: isLoyaltyIncentivesLoading } = useLoyaltyIncentives();

  return isLoyaltyIncentivesLoading || !loyaltyUserReady || isChallengesLoading;
};

export const useShouldHideChallenges = () => {
  const { isAuthenticated } = useAuthContext();
  const isChallengeUiEnabled = useFlag(LaunchDarklyFlag.ENABLE_CHALLENGE_UI);
  const { data: challengesData, isPending } = useChallengesUi();

  return (
    !isChallengeUiEnabled ||
    !isAuthenticated ||
    (challengesData && challengesData.length === 0) ||
    (!isPending && !challengesData)
  );
};

export const useShouldShowSectionsHeader = () => {
  const isDesktop = useIsDesktopBp();

  const shouldHideChallenges = useShouldHideChallenges();

  const isLoading = useIsLoyaltyPageLoading();

  return !isDesktop && !shouldHideChallenges && !isLoading;
};

export const useSections = (activeSection: Section, onPress: (section: Section) => void) => {
  const activeChallengesBadge = useActiveChallengesBadge();
  return [
    {
      id: 'challenges',
      title: 'Challenges',
      onPress: () => onPress('challenges'),
      isActive: activeSection === 'challenges',
      badge: activeChallengesBadge,
    },
    {
      id: 'offers',
      title: 'Offers',
      onPress: () => onPress('offers'),
      isActive: activeSection === 'offers',
    },
    {
      id: 'rewards',
      title: 'Rewards',
      onPress: () => onPress('rewards'),
      isActive: activeSection === 'rewards',
    },
  ];
};
